import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Locale, TranslationUnitView } from '@icetus/contracts';
import { LOCALE_LABEL, TRANSLATION_REASON_LABEL } from '@icetus/contracts';
import { Badge, Button, Card } from '@icetus/ui';
import {
  fetchTranslationUnits,
  patchTranslationUnit,
  publishTranslation,
  revertTranslationUnit,
} from '../api';
import { useConfirm } from '../components/confirm';

/**
 * 双语对照校对。
 *
 * 这一页的目标不是"展示译文"，而是**让客户在十分钟内把该看的一百条看完**。
 * 所以整页只围绕三件事设计：
 *
 *   1. 一屏能看多少条（源文与译文左右并排、一行一条，不做卡片套卡片）
 *   2. 改完不用点保存（失焦即存 + Ctrl/⌘+Enter 立即存）
 *   3. 只看看得懂的信号（行首的徽标说"这条是机器翻的 / 你改过"，不是一堆字段名）
 *
 * 刻意不做的事：
 *   · 不做「全部保存」按钮。批量保存意味着一次失败的请求会拖掉一百条修改，
 *     而客户根本不知道该重试哪一条。
 *   · 不做「一键机翻全部待复核」。那等于把未经人看的译文直接发布，
 *     而这页存在的意义就是不要那样。
 *   · 不显示翻译时间戳、模型名等内部信息（客户不关心 hex 指纹，也不关心我们用的哪个模型）。
 */

type Filter = 'all' | 'review' | 'human';

const FILTER_LABEL: Record<Filter, string> = {
  all: '全部',
  review: '需要看一眼',
  human: '我改过的',
};

/** 一次渲染多少条。几百条 textarea 同时挂上去会让输入明显卡顿 */
const PAGE_SIZE = 40;

export function TranslationReviewPage({
  tenantId,
  siteId,
  locale,
  onBack,
  onChanged,
}: {
  tenantId: string;
  siteId: string;
  locale: Locale;
  onBack: () => void;
  /** 写回成功后通知外层刷新总览（进度会变） */
  onChanged: () => void;
}) {
  const [units, setUnits] = useState<TranslationUnitView[] | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [query, setQuery] = useState('');
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const { confirm, confirmDialog } = useConfirm();

  const load = useCallback(async () => {
    try {
      const items = await fetchTranslationUnits(tenantId, { siteId, locale, filter });
      setUnits(items);
      setError(null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [tenantId, siteId, locale, filter]);

  useEffect(() => {
    setUnits(null);
    setLimit(PAGE_SIZE);
    void load();
  }, [load]);

  const filtered = useMemo(() => {
    if (!units) return [];
    const needle = query.trim().toLowerCase();
    if (!needle) return units;
    return units.filter(
      (unit) =>
        unit.source.toLowerCase().includes(needle) ||
        unit.target.toLowerCase().includes(needle) ||
        unit.entityLabel.toLowerCase().includes(needle),
    );
  }, [units, query]);

  const shown = filtered.slice(0, limit);

  /** 单条保存：更新列表里的那一行，避免整页重拉导致滚动位置丢失 */
  const applyLocal = (memoryId: string, patch: Partial<TranslationUnitView>) => {
    setUnits((prev) =>
      prev ? prev.map((unit) => (unit.memoryId === memoryId ? { ...unit, ...patch } : unit)) : prev,
    );
  };

  const onPublish = async () => {
    const ok = await confirm({
      title: `发布 ${LOCALE_LABEL[locale]} 的全部草稿页？`,
      message:
        '这会把这些页面推到前台。还没校对的段落也会一起上线 —— 之后校对改完会立刻生效，不用再发布一次。',
      confirmText: '发布',
      cancelText: '再改改',
    });
    if (!ok) return;
    setBusy(true);
    try {
      const result = await publishTranslation(tenantId, siteId, locale);
      setNotice(`已发布 ${result.published} 个页面。`);
      onChanged();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="stack">
      <div className="tablebar">
        <div className="review-head">
          <Button variant="ghost" size="sm" onClick={onBack}>
            ← 返回总览
          </Button>
          <strong>{LOCALE_LABEL[locale]} 校对</strong>
          {units ? (
            <span className="muted">
              共 {units.length} 段
              {query || filter !== 'all' ? `，筛出 ${filtered.length} 段` : ''}
            </span>
          ) : null}
        </div>
        <div className="tablebar__right">
          <Button variant="ghost" disabled={busy} onClick={() => void onPublish()}>
            发布这个语言的页面
          </Button>
        </div>
      </div>

      {error ? <p className="error">{error}</p> : null}
      {notice ? <p className="notice notice--ok">{notice}</p> : null}

      <Card
        flush
        title="逐条对照"
        description="左边是原文、右边是译文，改完点别处就自动保存。原文本身请到「页面内容」里改 —— 这里只改译文。"
        actions={
          <div className="review-tools">
            <div className="segmented">
              {(Object.keys(FILTER_LABEL) as Filter[]).map((key) => (
                <button
                  key={key}
                  type="button"
                  className={`segmented__item${filter === key ? ' is-active' : ''}`}
                  onClick={() => setFilter(key)}
                >
                  {FILTER_LABEL[key]}
                </button>
              ))}
            </div>
            <input
              className="field review-search"
              placeholder="搜原文、译文或页面名…"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setLimit(PAGE_SIZE);
              }}
            />
          </div>
        }
      >
        {!units ? (
          <p className="muted review-empty">加载中…</p>
        ) : shown.length === 0 ? (
          <p className="muted review-empty">
            {units.length === 0
              ? filter === 'human'
                ? '你还没有手工改过任何一条译文。'
                : filter === 'review'
                  ? '没有需要复核的段落 —— 模型对这批译文的把握都还行。'
                  : '这个语言还没有译文。先去总览页发起一次翻译。'
              : '没有匹配的段落。'}
          </p>
        ) : (
          <ul className="review-list">
            {shown.map((unit) => (
              <ReviewRow
                key={unit.memoryId}
                tenantId={tenantId}
                unit={unit}
                onPatched={(target) => {
                  applyLocal(unit.memoryId, { target, origin: 'human' });
                  onChanged();
                }}
                onReverted={() => {
                  /**
                   * 还原之后这条的文本变成"服务端那边的机器译文"，客户端并不知道是
                   * 哪个字符串。重拉一次比猜准 —— 而且重拉还能把 `canRevert`
                   * 一起更新掉（还原完它就该变成 false）。
                   */
                  void load();
                  onChanged();
                }}
                onError={(message) => setError(message)}
              />
            ))}
          </ul>
        )}

        {filtered.length > shown.length ? (
          <div className="review-more">
            <Button variant="ghost" onClick={() => setLimit((prev) => prev + PAGE_SIZE)}>
              再看 {Math.min(PAGE_SIZE, filtered.length - shown.length)} 条（还有{' '}
              {filtered.length - shown.length} 条）
            </Button>
          </div>
        ) : null}
      </Card>

      {confirmDialog}
    </section>
  );
}

/**
 * 一行：源文 / 译文 / 状态。
 *
 * 用 textarea 而不是 input：译文经常两三行，单行框会把后面的字藏起来，
 * 客户以为译文被截断了。`rows` 按字符长度估算，让短句不浪费空间。
 */
function ReviewRow({
  tenantId,
  unit,
  onPatched,
  onReverted,
  onError,
}: {
  tenantId: string;
  unit: TranslationUnitView;
  onPatched: (target: string) => void;
  onReverted: () => void;
  onError: (message: string) => void;
}) {
  const [value, setValue] = useState(unit.target);
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const savedRef = useRef(unit.target);

  /**
   * 外层重新拉数据（切筛选、切语言、还原之后）时同步一次。
   *
   * 只在"本地没有未保存改动"时同步：否则客户正在打字，父组件因为别的原因
   * 重渲染了一次，刚敲进去的字会被库里那版盖掉。
   * 依赖只写 `unit.target` 是刻意的 —— `value` 进依赖会让这个 effect
   * 每敲一个字就重跑一次，反而变成"打字时被自己覆盖"。
   */
  useEffect(() => {
    if (value === savedRef.current) {
      setValue(unit.target);
      savedRef.current = unit.target;
    }
  }, [unit.target]);

  const save = async () => {
    const next = value.trim();
    if (next === savedRef.current.trim()) return;
    if (next === '') {
      // 空译文会被服务端拒（400），前端先说清楚，并把值退回去
      onError('译文不能为空。要清空请直接删掉这条内容，或还原为机器译文。');
      setValue(savedRef.current);
      return;
    }
    setStatus('saving');
    try {
      await patchTranslationUnit(tenantId, unit.memoryId, next);
      savedRef.current = next;
      setStatus('saved');
      onPatched(next);
      window.setTimeout(() => setStatus('idle'), 1600);
    } catch (err: unknown) {
      setStatus('idle');
      onError(err instanceof Error ? err.message : String(err));
    }
  };

  const revert = async () => {
    setStatus('saving');
    try {
      await revertTranslationUnit(tenantId, unit.memoryId);
      // 还原后服务端会写回机器译文，这里没有它的值 —— 让外层重拉最稳妥
      onReverted();
      setStatus('saved');
      window.setTimeout(() => setStatus('idle'), 1600);
    } catch (err: unknown) {
      setStatus('idle');
      onError(err instanceof Error ? err.message : String(err));
    }
  };

  const rows = Math.min(6, Math.max(2, Math.ceil(value.length / 52)));

  return (
    <li className="review-row">
      <div className="review-row__source">
        <div className="review-row__meta">
          <span className="review-row__where">
            {unit.entityLabel}
            <span className="dot-sep">·</span>
            {unit.fieldLabel}
          </span>
        </div>
        <p className="review-row__text" lang="en">
          {unit.source}
        </p>
      </div>

      <div className="review-row__target">
        <div className="review-row__meta">
          {unit.origin === 'human' ? (
            <Badge tone="success">你改过</Badge>
          ) : (
            /* 只区分「AI 翻的 / 你改过」，具体哪个模型是内部运维信息 */
            <Badge tone="neutral">AI 翻译</Badge>
          )}
          {unit.checks.map((check) => (
            <span key={check.code} className="review-row__flag" title={TRANSLATION_REASON_LABEL[check.code]}>
              {TRANSLATION_REASON_LABEL[check.code]}
            </span>
          ))}
          <span className="review-row__spacer" />
          {status === 'saving' ? <span className="muted">保存中…</span> : null}
          {status === 'saved' ? <span className="review-row__saved">已保存</span> : null}
          {unit.canRevert ? (
            <Button variant="subtle" size="sm" onClick={() => void revert()}>
              还原为 AI 译文
            </Button>
          ) : null}
        </div>
        <textarea
          className="field review-row__input"
          rows={rows}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={() => void save()}
          onKeyDown={(e) => {
            // Ctrl/⌘+Enter 立即保存：客户习惯性会去按，不给反馈会以为没存上
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
              e.preventDefault();
              void save();
            }
          }}
        />
      </div>
    </li>
  );
}
